
import StyleProvider from '@/StyleProvider.vue';
import TheApp from './TheApp.vue';
import Vue, { PropType } from 'vue';

import VModal from 'vue-js-modal';
import PortalVue from 'portal-vue';

import { mapActions, mapState, PiniaVuePlugin } from 'pinia';
import { useStore as useLangStore } from '@/stores/lang';
import { useStore as useAuthStore } from '@/stores/auth';
import i18n from '@/i18n';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import { priceDetails, pdpChoices } from '@/setters';
import { SupportedLangCodes } from '../../constants/languageCodes';
import { SystemMonitor } from '../../components/Notifiers';
import { pinia } from '@/pinia';
import { ProductPrintPositionAddModel } from '@/typings';
import { LogoOnlyArtwork } from './typings';
import PreviewPdf from '../../components/SwitchColor/PreviewPdf.vue';
import WhatsNew from '../../components/WhatsNew/WhatsNew.vue';
import { CreatePersonalizationFormWrapper } from './typings/common/createPersonalization';

Vue.use(PiniaVuePlugin);
Vue.use(VModal, {
  dynamic: true,
  injectModalsContainer: true,
});
Vue.use(PortalVue, {
  portalName: 'w2p-portal',
  portalTargetName: 'w2p-portal-target',
});

export default Vue.extend({
  name: 'WcExportApp',
  components: {
    StyleProvider,
    TheApp,
    SystemMonitor,
    PreviewPdf,
    WhatsNew,
  },

  data() {
    return {
      showPreview: false,
      triggerSmallDetailsCheck: false,
    };
  },

  props: {
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'none',
      validator: isValidLanguageValidator,
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    priceDetails,
    pdpChoices,
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
    isDirectApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFirstTimeOpened: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConceptDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOrderDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    printPositions: {
      type: [] as PropType<ProductPrintPositionAddModel[]>,
      required: false,
      default() {
        return [];
      },
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    logoOnlyArtwork: {
      type: [] as PropType<LogoOnlyArtwork[]>,
      required: false,
      default: () => {
        return [];
      },
    },
    version: {
      type: String,
      required: false,
      default: 'V1',
    },
    context: {
      type: String,
      required: false,
      default: '',
    },
    personalizationJson: {
      type: Object as PropType<CreatePersonalizationFormWrapper>,
      required: false,
      default: () => {
        return {};
      },
    },
    userLoginOptions: {
      type: Object as PropType<{ data: string; iv: string }>,
      required: false,
      default: () => {
        return { data: '', iv: '' };
      },
    },
  },

  computed: {
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useAuthStore, ['isTokValid']),
  },

  watch: {
    isTokValid(newValue) {
      if (!newValue) {
        this.$emit('token-invalid-or-expired');
      }
    },

    async tok(newValue) {
      if (newValue !== '') {
        this.setTok(this.tok);
        await this.setDefaultTranslations();
      }
    },
  },

  async mounted() {
    this.setLanguageLocale(this.languageCode);
    this.setSapCustomerId(this.sapCustomerId);
    this.setTok(this.tok);
    await this.setDefaultTranslations();

    window.callConstructPersonalizationJSON =
      this.callConstructPersonalizationJSON;
  },

  methods: {
    ...mapActions(useLangStore, [
      'setLanguageLocale',
      'setDefaultTranslations',
    ]),
    ...mapActions(useAuthStore, ['setSapCustomerId', 'setTok']),

    showPdfPreview() {
      this.showPreview = true;
    },

    // NOTE: Leave, need it for bug fixing
    emitEvent(eventName: string, event: any) {
      if (eventName === 'wizard:price-params-changed') {
        this.$emit('wizard:price-params-changed', event);
      }

      if (eventName === 'wizard:back') {
        this.$emit('wizard:back');
      }
    },
    closePreview() {
      this.showPreview = false;
    },

    async callConstructPersonalizationJSON() {
      // Accessing the child component method using $refs
      const childComponent = this.$refs.theAppComponent as any;
      if (
        childComponent &&
        typeof childComponent.constructPersonalizationJSON === 'function'
      ) {
        console.log('callConstructPersonalizationJSON');
        const result = await childComponent.constructPersonalizationJSON();
        return result;
      } else {
        console.log('Method callConstructPersonalizationJSON not available');
        return null;
      }
    },
  },

  i18n,
  pinia,
});
